// utils
import { addDays, isAfter, isBefore, max, startOfDay } from 'date-fns';
import { parseBackendDateTime } from 'utils/date';

// types
import type { RegisterAbsenceWorkAgreement } from 'RestAPIs/roster/register-absence';

/**
 * An inschaling that changes inside the chosen period, per work agreement.
 *
 * - `ends`: the current inschaling stops inside the period. `validTo` is
 *   exclusive — the next inschaling starts at that very moment (measured in
 *   the dzz data: 914 of 947 adjoining benefits start on the previous
 *   `valid_to`, 00:00) — so `date` is the first day of the new inschaling and
 *   `lastDay` the last day of the current one (see `lastDayOf`).
 * - `starts`: the inschaling only begins inside the period, so there is no
 *   end to run the absence up to.
 */
export type BenefitChange = {
  waId: number;
  nr?: number;
  kind: 'ends' | 'starts';
  /** the first day under the other inschaling */
  date: Date;
  /** `ends` only: the last day under the current inschaling */
  lastDay?: Date;
};

/**
 * The last day under an inschaling that ends at `validTo`. A `valid_to` at
 * 00:00 hands the whole day to the next inschaling, so the day before is the
 * last; one later in the day leaves that day partly under the current one, so
 * that day is the last. Never before the start of the period: "until the end of
 * the inschaling" must not put the end date before Van.
 */
const lastDayOf = (validTo: Date, startDate: Date) => {
  const day = startOfDay(validTo);
  const last = day.getTime() === validTo.getTime() ? addDays(day, -1) : day;
  return max([last, startOfDay(startDate)]);
};

/**
 * The inschaling changes inside the period when the benefit that the endpoint
 * returned does not span it (MyBatis collapses the rows per work agreement, so
 * the validity of that one benefit is the only signal).
 */
export const getBenefitChanges = (
  rows: RegisterAbsenceWorkAgreement[],
  startDate: Date | null,
  endDate: Date | null
): BenefitChange[] => {
  if (!startDate || !endDate) {
    return [];
  }
  const changes = new Map<number, BenefitChange>();
  for (const row of rows) {
    const validTo = row.validTo ? parseBackendDateTime(row.validTo) : null;
    const validFrom = row.validFrom
      ? parseBackendDateTime(row.validFrom)
      : null;
    const lastDay =
      validTo && isAfter(validTo, startDate)
        ? lastDayOf(validTo, startDate)
        : null;
    // measured against that last day, not against valid_to itself: a period
    // that runs up to it no longer crosses the end, so "until the end of the
    // inschaling" clears the notice also when valid_to falls later in a day
    if (validTo && lastDay && isBefore(lastDay, startOfDay(endDate))) {
      changes.set(row.waId, {
        waId: row.waId,
        nr: row.nr,
        kind: 'ends',
        date: validTo,
        lastDay
      });
    } else if (
      validFrom &&
      isAfter(validFrom, startDate) &&
      // an end found on another row of this work agreement says more: it
      // offers the jump to that end
      changes.get(row.waId)?.kind !== 'ends'
    ) {
      changes.set(row.waId, {
        waId: row.waId,
        nr: row.nr,
        kind: 'starts',
        date: validFrom
      });
    }
  }
  return [...changes.values()];
};

/** the change date per work agreement, for the places that only need that */
export const byWorkAgreement = (changes: BenefitChange[]) =>
  Object.fromEntries(
    changes.map((change) => [change.waId, change.date])
  ) as Record<number, Date>;
